import React from "react";
import { MdFactCheck } from "react-icons/md";
import { FaFileAlt, FaFileSignature, FaClipboardCheck } from "react-icons/fa";
import { ImArrowRight, ImArrowDown, ImArrowLeft } from "react-icons/im";
import { HiOutlineDocumentText } from "react-icons/hi";
import { LiaCertificateSolid } from "react-icons/lia";

const ICVCertification = () => {
  const services = [
    {
      icon: <FaFileAlt className="text-3xl" />,
      title: "Application Form",
      bg: "bg-[#E6F8FF]",
      color: "text-[#29AAE1]",
    },
    {
      icon: <FaFileSignature className="text-3xl" />,
      title: "Quotation",
      bg: "bg-[#FFF2E6]",
      color: "text-[#FF8A3D]",
    },
    {
      icon: <HiOutlineDocumentText className="text-3xl" />,
      title: "Manual & Procedure",
      bg: "bg-[#F3E6FF]",
      color: "text-[#9C27B0]",
    },
    {
      icon: <FaClipboardCheck className="text-3xl" />,
      title: "Audit",
      bg: "bg-[#E6FFED]",
      color: "text-[#00C853]",
    },
    {
      icon: <LiaCertificateSolid className="text-3xl" />,
      title: "Certification",
      bg: "bg-[#F3E6FF]",
      color: "text-[#1E88E5]",
    },
    {
      icon: <MdFactCheck className="text-3xl" />,
      title: "Annual Surveillance",
      bg: "bg-[#E6FFED]",
      color: "text-[#FFC107]",
    },
  ];

  const ArrowComponent = ({ direction, className = "" }) => {
    const ArrowIcon =
      direction === "right"
        ? ImArrowRight
        : direction === "down"
        ? ImArrowDown
        : ImArrowLeft;

    return (
      <div className={`flex justify-center items-center ${className}`}>
        <div className="p-3 bg-white rounded-full shadow-md border-2 border-[#29AAE1]/20">
          <ArrowIcon className="text-[#29AAE1] text-2xl animate-pulse" />
        </div>
      </div>
    );
  };

  return (
    <section className="w-full bg-gradient-to-br from-[#F7FAFF] to-[#E6F4FF] py-10 px-4 lg:px-12">
      <div className="flex flex-col lg:flex-row gap-12 items-center">
        {/* Left Content */}
        <div className="lg:w-1/2 space-y-4 text-center lg:text-left">
          {/* Heading */}
          <div className="relative inline-block">
            <h2 className="text-3xl md:text-4xl font-bold text-[#032303] leading-loose">
              Welcome to ICV
              <br /> Certification{" "}
              <span className="text-[#29AAE1]">Services</span>
            </h2>
          </div>

          {/* Description */}
          <div className="space-y-6">
            <p className="text-lg text-[#4D4D4D] leading-relaxed transition-colors duration-300 hover:text-[#032303]">
              ICV Certification is your premier global partner for fast,
              effective, and economical certification solutions. We specialize
              in documentation, system awareness, auditor training, and
              consultancy.
            </p>

            <p className="text-lg text-[#4D4D4D] leading-relaxed transition-colors duration-300 hover:text-[#032303]">
              Our tailored ISO certification services empower businesses of all
              sizes to achieve compliance, enhance competitiveness, and meet
              rigorous international standards.
            </p>
          </div>
        </div>

        {/* Right Content */}
        <div className="lg:w-1/2 relative">
          <div className="relative h-full bg-[#249742]/10 rounded-3xl p-6 sm:p-10 flex flex-col justify-center">
            {/* Heading */}
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#032303] mb-8">
              Certification Process
            </h2>

            {/* Services with S-pattern flow for larger screens */}
            <div className="hidden sm:block">
              {/* Row 1: Application Form -> Right Arrow -> Quotation */}
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`flex items-center gap-4 p-4 rounded-xl ${services[0].bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex-1`}
                >
                  <span
                    className={`p-3 rounded-full bg-white shadow-sm ${services[0].color}`}
                  >
                    {services[0].icon}
                  </span>
                  <span className="text-lg font-semibold text-[#032303]">
                    {services[0].title}
                  </span>
                </div>

                <ArrowComponent direction="right" className="mx-1" />

                <div
                  className={`flex items-center gap-4 p-4 rounded-xl ${services[1].bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex-1`}
                >
                  <span
                    className={`p-3 rounded-full bg-white shadow-sm ${services[1].color}`}
                  >
                    {services[1].icon}
                  </span>
                  <span className="text-lg font-semibold text-[#032303]">
                    {services[1].title}
                  </span>
                </div>
              </div>

              {/* Down Arrow after Quotation */}
              <div className="flex justify-end mb-6">
                <div className="w-1/2 flex justify-center">
                  <ArrowComponent direction="down" />
                </div>
              </div>

              {/* Row 2: Manual & Procedure <- Left Arrow <- Audit */}
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`flex items-center gap-4 p-4 rounded-xl ${services[3].bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex-1`}
                >
                  <span
                    className={`p-3 rounded-full bg-white shadow-sm ${services[3].color}`}
                  >
                    {services[3].icon}
                  </span>
                  <span className="text-lg font-semibold text-[#032303]">
                    {services[3].title}
                  </span>
                </div>

                <ArrowComponent direction="left" className="mx-1" />

                <div
                  className={`flex items-center gap-4 p-4 rounded-xl ${services[2].bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex-1`}
                >
                  <span
                    className={`p-3 rounded-full bg-white shadow-sm ${services[2].color}`}
                  >
                    {services[2].icon}
                  </span>
                  <span className="text-lg font-semibold text-[#032303]">
                    {services[2].title}
                  </span>
                </div>
              </div>

              {/* Down Arrow after Manual & Procedure */}
              <div className="flex justify-start mb-6">
                <div className="w-1/2 flex justify-center">
                  <ArrowComponent direction="down" />
                </div>
              </div>

              {/* Row 3: Annual Surveillance -> Right Arrow -> Certification */}
              <div className="flex items-center justify-between">
                <div
                  className={`flex items-center gap-4 p-4 rounded-xl ${services[4].bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex-1`}
                >
                  <span
                    className={`p-3 rounded-full bg-white shadow-sm ${services[4].color}`}
                  >
                    {services[4].icon}
                  </span>
                  <span className="text-lg font-semibold text-[#032303]">
                    {services[4].title}
                  </span>
                </div>

                <ArrowComponent direction="right" className="mx-1" />

                <div
                  className={`flex items-center gap-4 p-4 rounded-xl ${services[5].bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex-1`}
                >
                  <span
                    className={`p-3 rounded-full bg-white shadow-sm ${services[5].color}`}
                  >
                    {services[5].icon}
                  </span>
                  <span className="text-lg font-semibold text-[#032303]">
                    {services[5].title}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Layout - Single Column with Down Arrows */}
            <div className="sm:hidden space-y-4">
              {services.map((item, index) => (
                <div key={index}>
                  <div
                    className={`flex items-center gap-4 p-4 rounded-xl ${item.bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
                  >
                    <span
                      className={`p-3 rounded-full bg-white shadow-sm ${item.color}`}
                    >
                      {item.icon}
                    </span>
                    <span className="text-lg font-semibold text-[#032303]">
                      {item.title}
                    </span>
                  </div>
                  {/* Add down arrow between items except after the last one */}
                  {index < services.length - 1 && (
                    <div className="flex justify-center my-3">
                      <ArrowComponent direction="down" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Decorative Floating Elements */}
            <div className="absolute bottom-8 right-8 w-24 h-24 bg-[#29AAE1] bg-opacity-20 rounded-full filter blur-xl pointer-events-none"></div>
            <div className="absolute top-8 right-16 w-16 h-16 bg-[#249742] bg-opacity-20 rounded-full filter blur-xl pointer-events-none"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ICVCertification;
